import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Rating,
  TextField,
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
} from "@mui/material";
import { useParams } from "react-router-dom";
import apiService from "../../../../service/Api";

const BookingReviewPage: React.FC = () => {
  const { reference_number } = useParams<{ reference_number: string }>();

  const [rating, setRating] = useState<number | null>(0);
  const [comment, setComment] = useState("");
  const [improvements, setImprovements] = useState("");

  const [wouldRecommend, setWouldRecommend] = useState<"yes" | "no" | "maybe">(
    "yes",
  );

  const [recommendationReason, setRecommendationReason] = useState("");

  const [submitting, setSubmitting] = useState(false);

  const submitReview = async () => {
    if (!reference_number) return;

    try {
      setSubmitting(true);

      await apiService.post(`/api/bookings/booking-review/`, {
        reference_number: reference_number,
        client_review: rating,
        comment,
        improvement_suggestions: improvements
          ? improvements.split(",").map((s) => s.trim())
          : [],
        would_recommend: wouldRecommend,
        recommendation_reason: recommendationReason,
      });

      alert("Review submitted successfully");

      setRating(0);
      setComment("");
      setImprovements("");
      setWouldRecommend("yes");
      setRecommendationReason("");
    } catch (error) {
      console.error("Review submit error:", error);
      alert("Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box p={3} display="flex" justifyContent="center">
      <Card sx={{ width: "100%", maxWidth: 600 }}>
        <CardContent>
          <Typography variant="h5" fontWeight="bold" mb={2}>
            Booking Review
          </Typography>

          <Typography variant="body2" color="text.secondary" mb={3}>
            Reference: {reference_number}
          </Typography>

          <Stack spacing={3}>
            {/* Rating */}
            <Box>
              <Typography fontWeight="bold">Overall Rating</Typography>
              <Rating
                value={rating}
                onChange={(_, newValue) => setRating(newValue)}
                size="large"
              />
            </Box>

            <Divider />

            {/* Comment */}
            <TextField
              label="Your Comment"
              multiline
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              fullWidth
            />

            {/* Improvement Suggestions */}
            <TextField
              label="Improvement Suggestions (comma separated)"
              multiline
              rows={3}
              value={improvements}
              onChange={(e) => setImprovements(e.target.value)}
              fullWidth
              helperText="Example: better timing, clearer communication, more staff"
            />

            <Divider />

            {/* Recommendation */}
            <Box>
              <Typography fontWeight="bold" mb={1}>
                Would you recommend us?
              </Typography>

              <RadioGroup
                row
                value={wouldRecommend}
                onChange={(e) =>
                  setWouldRecommend(e.target.value as "yes" | "no" | "maybe")
                }
              >
                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                <FormControlLabel
                  value="maybe"
                  control={<Radio />}
                  label="Maybe"
                />
                <FormControlLabel value="no" control={<Radio />} label="No" />
              </RadioGroup>
            </Box>

            {/* Reason */}
            <TextField
              label="Reason for recommendation (optional)"
              multiline
              rows={3}
              value={recommendationReason}
              onChange={(e) => setRecommendationReason(e.target.value)}
              fullWidth
            />

            {/* Submit */}
            <Button
              variant="contained"
              size="large"
              disabled={submitting}
              onClick={submitReview}
            >
              {submitting ? "Submitting..." : "Submit Review"}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BookingReviewPage;
